/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable operator-linebreak */
import { useState, useEffect, ReactElement } from 'react';
import { useLocation } from 'react-router-dom';
import { useIdleTimer } from 'react-idle-timer';
import { Modal, Button } from '@components';
import { navigateToError } from '@utils/Navigation';
import styles from './IdleTimer.module.scss';

const {
  REACT_APP_SITECORE_API_ENDPOINT: API_URL,
  REACT_APP_PATH_THIRD_PARTY: PATH_THIRD_PARTY,
  REACT_APP_PATH_COURT: PATH_COURT,
  REACT_APP_PATH_EXTENSION: PATH_EXTENSION,
  REACT_APP_PATH_FAMILY_VIOLENCE_SCHEME: PATH_FVS,
  REACT_APP_PATH_NOMINATION: PATH_NOMINATION,
  REACT_APP_PATH_INSTALMENT: PATH_INSTALMENT,
  REACT_APP_PATH_REVIEW: PATH_REVIEW,
} = process.env;

const idleTimer = (): ReactElement => {
  const TIMEOUT_MINUTES_UNTIL_WARNING = 10;
  const TIMEOUT_SECONDS_WARNING = 600;
  const location = useLocation();

  const [countdown, setCountdown] = useState(TIMEOUT_SECONDS_WARNING);
  const [countdownActive, setCountdownActive] = useState(false);
  const [hasTimedOut, setHasTimedOut] = useState(false);
  let timeout: ReturnType<typeof setTimeout>;

  const continueSession = () => {
    clearTimeout(timeout);
    setCountdownActive(false);
    setCountdown(TIMEOUT_SECONDS_WARNING);
  };

  const exitToHome = async () => {
    const apiLogout = `${API_URL}sitecore/Login/Logout`;
    let response: any;
    try {
      response = await fetch(apiLogout, {
        method: 'POST',
      });
      const res = await response.json();
      if (res.result) {
        if (location.pathname.includes(PATH_COURT!)) window.location.href = `${PATH_COURT}`;
        else if (location.pathname.includes(PATH_THIRD_PARTY!)) window.location.href = `${PATH_THIRD_PARTY}`;
        else if (location.pathname.includes(PATH_EXTENSION!)) window.location.href = `${PATH_EXTENSION}`;
        else if (location.pathname.includes(PATH_FVS!)) window.location.href = `${PATH_FVS}`;
        else if (location.pathname.includes(PATH_NOMINATION!)) window.location.href = `${PATH_NOMINATION}`;
        else if (location.pathname.includes(PATH_INSTALMENT!)) window.location.href = `${PATH_INSTALMENT}`;
        else if (location.pathname.includes(PATH_REVIEW!)) window.location.href = `${PATH_REVIEW}`;
        else navigateToError();
      }
    } catch (err) {
      navigateToError();
    }
  };

  const handleOnIdle = () => {
    setCountdownActive(true);
  };

  useIdleTimer({
    timeout: 1000 * 60 * TIMEOUT_MINUTES_UNTIL_WARNING,
    onIdle: handleOnIdle,
    debounce: 500,
  });

  const sessionTimeOutModalElement = (): ReactElement => (
    <>
      <div className={styles.timeOutInfo}>We have not detected any activity from you within the last 10 minutes, as a security measure we will log you out shortly. </div>
      <hr />
      <div className={styles.idleTimer}>
        {countdown > 60
          ? `Your session will expire in ${Math.floor(countdown / 60)} minute${Math.floor(countdown / 60) > 1 ? 's' : ''} ${countdown % 60} second${countdown % 60 > 1 ? 's' : ''}`
          : `Your session will expire in ${countdown} second${countdown > 1 ? 's' : ''}`}
      </div>
      <hr />
      <div className={styles.timeOutInstructions}>
        Press the <b>spacebar</b> or click <b>OK</b> to resume.
      </div>
      <div className={styles.btnFooter}>
        <Button
          className={styles.btnProceed}
          onClick={() => {
            continueSession();
          }}
          color="blue"
          size="small">
          OK
        </Button>
      </div>
    </>
  );

  const sessionTimedOutElement = (): React.ReactElement => (
    <>
      <div>We did not detect any activity from you in the last 10 minutes and have now logged you out of this session as a security measure.</div>
      <br />
      <div>
        Click <b>OK</b> to close this window and login again to resume.
      </div>
      <div className={styles.btnFooter}>
        <Button
          onClick={() => {
            exitToHome();
          }}
          color="blue"
          size="small">
          OK
        </Button>
      </div>
    </>
  );
  const isFVSTimerExist: boolean =
    location.pathname.toLowerCase().localeCompare(`${PATH_FVS}`.toLowerCase()) !== 0 && location.pathname.toLowerCase().localeCompare(`${PATH_FVS}/step1`.toLowerCase()) !== 0;
  useEffect(() => {
    if (!isFVSTimerExist) {
      setCountdown(TIMEOUT_SECONDS_WARNING);
      setCountdownActive(false);
      setHasTimedOut(false);
      return;
    }
    if (isFVSTimerExist) {
      if (countdown > 0 && countdownActive) {
        timeout = setTimeout(() => setCountdown(countdown - 1), 1000);
      }
      if (countdown === 0) {
        document.cookie.split(';').forEach((c) => {
          document.cookie = c.replace(/^ +/, '').replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/`);
        });
        setHasTimedOut(true);
      }

      document.body.onkeyup = function (e) {
        if (e.code === 'Space') {
          e.preventDefault();
          continueSession();
        }
      };
    }
  }, [countdownActive, countdown, isFVSTimerExist]);
  if (hasTimedOut && isFVSTimerExist) {
    return <Modal close={() => exitToHome()} show title="Session is now expired" content={sessionTimedOutElement()} />;
  }
  if (countdownActive && isFVSTimerExist) {
    return <Modal close={() => continueSession()} show title="Session timeout" content={sessionTimeOutModalElement()} />;
  }
  return <></>;
};

export default idleTimer;
